import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import LayoutIcon from "../../assets/Fibo/icons/LayoutIcon";
import { POSTHOG_EVENTS } from "../../constants/PostHogEvents";
import { usePostHog } from "../../hooks/usePostHog";
import { useAppStore } from "../../hooks/useStores";
import LabResults from "../../layout/LabsLayout/Results/LabResults";
import { PlaygroundImage } from "../../models/image-to-image";
import { getFiboLabVariant } from "../../utils";
import LabExperiments from "../Labs/Experiments/LabExperiments";
import { PlaygroundVideoProps } from "../Playground/VideoGeneration/Video/PlaygroundVideo";
import FiboConfig from "./Config/FiboConfig";
import styles from "./Fibo.module.scss";
import { useFiboController } from "./hooks/useFiboController";
import { useScrollToSection } from "./hooks/useScrollToSection";
import TryRefinePopup from "./TryRefinePopup/TryRefinePopup";

const Fibo = () => {
	const { registerSection, jumpToSection } = useScrollToSection();
	const { playgroundStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const labVariant = getFiboLabVariant();

	// Track page view
	useEffect(() => {
		if (labVariant) {
			trackEvent(POSTHOG_EVENTS.FIBO_LAB_PAGE_VIEWED, {
				lab_variant: labVariant,
			});
		}
	}, [labVariant, trackEvent]);
	const {
		activeTab,
		isRefineTabEnabled,
		handleTabSwitch,
		handleResultInView,
		handleJumpTo,
		showTryRefinePopup,
		handleTryRefineClick,
		handleTryRefinePopupClose,
		handleReferenceImageClick,
	} = useFiboController();
	const { t } = useTranslation("fibo", { keyPrefix: "lab.images" });

	const FiboEmptyResultsElement = (): JSX.Element => {
		return (
			<Box className={styles.emptyContainerContent}>
				<LayoutIcon />
				<Typography className={styles.title}>{t("title")}</Typography>
				<Typography className={styles.description}>{t("description")}</Typography>
			</Box>
		);
	};

	return (
		<Box className={styles.container}>
			{showTryRefinePopup && (
				<TryRefinePopup onClick={handleTryRefineClick} onClose={handleTryRefinePopupClose} />
			)}
			<Box className={styles.mainContent}>
				<FiboConfig
					activeTab={activeTab}
					setActiveTab={handleTabSwitch}
					isRefineTabEnabled={isRefineTabEnabled}
				/>
				<LabResults
					emptyElement={<FiboEmptyResultsElement />}
					onResultInView={handleResultInView}
					registerSection={registerSection}
					onReferenceImageClick={handleReferenceImageClick}
				/>
			</Box>

			{playgroundStore.labResultGroups.length > 0 && (
				<LabExperiments
					activeGroupId={playgroundStore.selectedResult?.id || undefined}
					onJumpTo={(resultId: string, selectedImage: PlaygroundImage | PlaygroundVideoProps) =>
						handleJumpTo(resultId, selectedImage, jumpToSection)
					}
				/>
			)}
		</Box>
	);
};

export default observer(Fibo);
